import { ref } from "vue";
import { BrandSneakPeek, SneakPeekBrand, SneakPeekProduct, SneakPeekProductCard } from "@client/modules/discovery/feature/brands-sneak-peek/types/types";
import { getBrandData, getProductData, getSneakPeek, getSneakPeekData } from "@client/modules/discovery/feature/brands-sneak-peek/services/api";
import { captureException } from '@core/plugins/sentry';

export const useSneakPeek = () => {
    const brandSneakPeeks = ref<BrandSneakPeek>({
        products: [],
        attributes: null
    });
    const totalProduct = ref<number>(0);
    const productIds = ref<string[]>([]);

    const fetchSneakPeekDataFromBrand = async (props: { brandId: string }) => {
        try {
            const response = await getSneakPeekData(props.brandId);
            
            // Extract brand from included array
            const brandCard = response.included?.find((item) => item.type === 'brand-card');
            
            // If no brand in included, try to get from first product's brand data
            let brandData = null;
            if (brandCard) {
                brandData = brandCard.attributes;
            } else if (response.data?.[0]?.attributes?.brand) {
                // Fallback: extract brand info from first product
                const firstProduct = response.data[0].attributes;
                brandData = {
                    brand_id: parseInt(props.brandId),
                    brand_name: firstProduct.brand.name,
                    brand_link: firstProduct.brand.link,
                    brand_logo: firstProduct.brand.images?.rounded || '',
                    brand_images: firstProduct.brand.images ? [{ src: firstProduct.brand.images.squared || firstProduct.brand.images.large }] : [],
                    instagram_followers: firstProduct.brand.instagram_followers,
                    instagram_username: firstProduct.brand.instagram_username,
                    country_code: firstProduct.brand.country_code || '',
                    story: firstProduct.brand.story || '',
                };
            }
            
            // Extract products from data array
            const productCards = response.data?.filter((item) => item.type === 'product-card') || [];
            
            // Transform brand data to minimal type
            const transformedBrand: SneakPeekBrand | null = brandData ? {
                brand_id: brandData.brand_id,
                brand_name: brandData.brand_name,
                brand_link: brandData.brand_link,
                brand_logo: brandData.brand_logo,
                brand_images: brandData.brand_images,
                instagram_followers: brandData.instagram_followers,
                instagram_username: brandData.instagram_username,
                country_code: brandData.country_code,
                story: brandData.story,
            } : null;
            
            // Transform product data to minimal type
            const transformedProducts: SneakPeekProductCard[] = productCards.slice(0, 4).map((product) => ({
                type: 'product-card' as const,
                id: product.id,
                attributes: {
                    id: product.id,
                    name: product.attributes.name,
                    link: product.attributes.link,
                    images: product.attributes.images,
                    wholesale_price: {
                        amount: product.attributes.wholesale_price?.amount || 0,
                        currency: product.attributes.wholesale_price?.currency || 'EUR',
                    },
                    discount_rate: product.attributes.discount_rate || 0,
                    brand: {
                        link: product.attributes.brand?.link || transformedBrand?.brand_link || '',
                    },
                } as SneakPeekProduct,
            }));
            
            // Set the transformed data
            brandSneakPeeks.value.attributes = transformedBrand;
            brandSneakPeeks.value.products = transformedProducts;
            totalProduct.value = productCards.length || 0;
            
            return response;
        } catch (error) {
            captureException('Error fetching sneak peek data', error);
            throw error;
        }
    }

    const fetchSneakPeekDataFromCMS = async (props: { brandId: string, carouselUuid?: string }) => {
        try {
            const results = await getSneakPeek(props.carouselUuid, props.brandId);
            
            // Handle the results array - find brand and product cards
            const brandCard = results.find((result) => result.type === 'brand-card');
            const productCards = results.filter((result) => result.type === 'product-card');
            
            // Set brand data in attributes (extract the attributes from brand card)
            brandSneakPeeks.value.attributes = brandCard?.attributes || null;
            
            // Set product cards in products array
            brandSneakPeeks.value.products = productCards.slice(0, 4);
            
            totalProduct.value = productCards.length || 0;
        } catch (error) {
            captureException('Error fetching sneak peek data', error);
            throw error;
        }
    }

    const fetchSneakPeekDataFromBrandAndProductsId = async (props: { brandId: string, productsId: string[] }) => {
        try {
             // Get brand attributes and transform to match SneakPeekBrand
            const brandData = await getBrandData(props.brandId);
            
            brandSneakPeeks.value.attributes = {
                brand_id: brandData.id,
                brand_name: brandData.name,
                brand_logo: brandData.images?.rounded || '',
                brand_link: brandData.link,
                country_code: brandData.country?.iso_code || '',
                story: brandData.story,
                instagram_followers: brandData.instagram_followers,
                instagram_username: brandData.instagram_username,
                brand_images: brandData.images ? Object.entries(brandData.images).map(([, value]) => ({
                    src: value,
                })) : []
            };
            
            if (props.productsId) {
                totalProduct.value = props.productsId.flat().length;
                const products = await Promise.all(props.productsId.slice(0, 5).map(async (productId) => {
                    const product = await getProductData(productId);
                    return {
                        type: 'product-card' as const,
                        id: productId,
                        attributes: {
                            id: productId,
                            name: product.name,
                            link: product.link,
                            images: product.images || [],
                            wholesale_price: {
                                amount: product.wholesale_price?.amount || 0,
                                currency: product.wholesale_price?.currency || 'EUR',
                            },
                            discount_rate: product.discount_rate || 0,
                            brand: {
                                link: product.brand?.link || '',
                            },
                        } as SneakPeekProduct,
                    } as SneakPeekProductCard;
                }));
                brandSneakPeeks.value.products = products.slice(0, 4);
            }
        } catch (error) {
            captureException('Error fetching sneak peek data', error);
        }
    }

  return {
      brandSneakPeeks,
      totalProduct,
      productIds,
      fetchSneakPeekDataFromCMS,
      fetchSneakPeekDataFromBrandAndProductsId,
      fetchSneakPeekDataFromBrand,
  };
};
